<template>
  <div class="md-layout">
    <div
      v-if="oldestApprovalRequest"
      class="md-layout-item md-medium-size-100 md-size-100"
    >
      <md-card>
        <md-card-header data-background-color="green">
          <h4 class="title">
            Solicitação de Aprovação
          </h4>
        </md-card-header>
        <md-card-content>
          <div class="md-layout">
            <div
              v-if="!isUserReader"
              class="md-layout-item md-small-size-100 md-size-100"
            >
              <md-field maxlength="5">
                <label>Descrição da Solicitação</label>
                <md-textarea
                  v-model="oldestApprovalRequest.comment_submission"
                />
              </md-field>
            </div>

            <div class="md-layout-item md-small-size-100 md-size-100">
              <md-field maxlength="5">
                <label>Comentário</label>
                <md-textarea
                  v-model="oldestApprovalRequest.comment_validation"
                />
              </md-field>
            </div>

            <div
              v-if="!isUserReader"
              class="md-layout-item md-size-100 text-center"
            >
              <md-button
                class="md-raised md-accent"
                @click="denyRequest()"
              >
                REJEITAR SOLICITAÇÃO
              </md-button>
              <Button
                button-classes="md-raised md-success"
                :disabled="!ticketInfo.due_date"
                @click="approveRequest()"
              >
                <template
                  v-if="!ticketInfo.due_date"
                  #tooltip
                >
                  <md-tooltip md-direction="top">
                    <p>
                      Não é possível realizar a aprovação, pois o chamado não
                      tem SLA cadastrado.
                    </p>
                  </md-tooltip>
                </template>
                APROVAR SOLICITAÇÃO
              </Button>
            </div>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <div
      v-if="ticketInfo"
      class="md-layout-item md-medium-size-100 md-size-100"
    >
      <step-progress
        class="step-progress"
        :steps="steps"
        :current-step="currentStep"
        active-color="green"
      />

      <ticket-service-evaluation
        v-if="
          (isSolvedTicket && isTicketApproved) || (isClosedTicket && rating)
        "
        :tech-rating="rating"
        :show-evaluation-options="!isClosedTicket"
        :show-comment-input="!isUserReader"
        :show-buttons="!isUserReader || !isUserClient"
        @approve="approveRating"
        @deny="denyRating"
      />

      <open-ticket-form
        :current-step="currentStep"
        :ticket-info="ticketInfo"
      />
    </div>

    <div
      v-if="latestTechLocationId"
      class="md-layout-item md-medium-size-100 md-size-100"
    >
      <md-card>
        <md-card-header data-background-color="green">
          <h4 class="title">
            Rastreamento do Técnico
          </h4>
        </md-card-header>

        <md-card-content>
          <tracking-tech :location-id="latestTechLocationId" />
        </md-card-content>
      </md-card>
    </div>

    <div
      v-if="ticketInfo && ticketInfo.documents.length > 0"
      class="md-layout-item md-medium-size-100 md-size-100"
    >
      <md-card>
        <md-card-header data-background-color="green">
          <h4 class="title">
            Documentos Anexos
          </h4>
        </md-card-header>
        <md-card-content>
          <div class="md-layout-item md-small-size-100 md-size-100">
            <md-list>
              <md-list-item
                v-for="doc in ticketInfo.documents"
                :key="doc.filepath"
                :href="doc.filepath"
              >
                <md-icon>file_present</md-icon>
                <div class="md-list-item-text">
                  {{ doc.filename }}
                </div>
              </md-list-item>
            </md-list>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <div
      v-if="tasks && tasks.length"
      class="md-layout-item md-medium-size-100 md-size-100"
    >
      <md-card>
        <md-card-header data-background-color="green">
          <h4 class="title">
            Atividades Relacionadas
          </h4>
        </md-card-header>
        <md-card-content>
          <div class="md-layout-item md-small-size-100 md-size-100">
            <div
              v-for="task in tasks"
              :key="task.id"
              class="md-layout-item md-size-100"
            >
              <md-field>
                <md-icon>task</md-icon>
                <label>{{ task.date }}</label>
                <md-textarea
                  v-model="task.content"
                  md-autogrow
                  disabled
                />
              </md-field>
            </div>
          </div>
        </md-card-content>
      </md-card>
    </div>
  </div>
</template>

<script>
import ms from "ms";
import { DateTime } from "luxon";

import StepProgress from "vue-step-progress";
import "vue-step-progress/dist/main.css";

import OpenTicketForm from "./GlpiForms/OpenTicketForm";
import TrackingTech from "../components/TrackingTech.vue";
import TicketServiceEvaluation from "../components/TicketServiceEvaluation.vue";
import Button from "../components/Button.vue";

import { userService } from "../_services";
import { getUserSession } from "../_services/localstorage";

import { ROUTES } from "../routes/routes";

import { strip } from "../util/strip";
import { isUserClient } from "../util/is-user-client";
import { isUserReader } from "../util/is-user-reader";
import { DateTimeFormats } from "../util/datetime.formats";
import { unescapeHtml } from "../util/unescape-html";

const TECH_POSITION_INTERVAL_MS = ms("1 minute");
const NOT_ANSWERED_APPROVAL_STATUS = 2;
const ACCEPTED_REQUEST_STATUS = 3;
const DENIED_REQUEST_STATUS = 4;

const TICKET_SOLVED_STATUS = 5;

export default {
  components: {
    StepProgress,
    OpenTicketForm,
    TrackingTech,
    TicketServiceEvaluation,
    Button,
  },
  data() {
    return {
      steps: [
        "Novo",
        "Atribuindo",
        "Atendimento",
        "Revisão",
        "Aprovação",
        "Fechado",
      ],
      currentStep: 0,

      // tech tracking
      latestTechLocationId: null,

      id: 0,
      ticketInfo: null,
      tasks: [],
      oldestApprovalRequest: null,

      // Ticket Evaluation
      technician: null,
      rating: 0,

      timer: null,
    };
  },
  computed: {
    isClosedTicket() {
      return this.ticketInfo.status === 6;
    },
    isSolvedTicket() {
      return this.ticketInfo.status === 5;
    },
    isUserReader() {
      return isUserReader(this.user);
    },
    isUserClient() {
      return isUserClient(this.user);
    },
    isTicketApproved() {
      return this.ticketInfo.approval === 3;
    },
  },
  async created() {
    this.id = this.$route.params.id;

    this.user = getUserSession();

    await this.fetchData();

    if (this.latestTechLocationId) {
      this.timer = setInterval(
        this.getTechLatestLocation,
        TECH_POSITION_INTERVAL_MS,
      ); // polling if location change
    }
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    getCurrentStep(currentStatus, approvalPercent) {
      let currentStep = currentStatus - 1 || 0; // convert to zero index based;
      // diff Revision and Approval steps
      if (currentStatus === TICKET_SOLVED_STATUS) {
        currentStep = approvalPercent === 0 ? 3 : 4;
      }
      return currentStep;
    },
    parseTask(task) {
      const taskContent = strip(task.content)
        .replaceAll("<br />", "\n")
        .replaceAll(/(<([^>]+)>)/gi, "");
      return {
        id: task.id,
        date: task.date,
        state: task.state,
        content: taskContent,
      };
    },
    parseFollowup(acc, followup) {
      if (!/#\s?Tecnico em deslocamento ao cliente/.test(followup.content)) {
        return acc;
      }

      const identifier = "ID da Localizacao: ";
      const locationId = RegExp(`${identifier}([0-9]+)`).exec(
        followup.content,
      )[1];

      return acc < locationId ? parseInt(locationId) : acc;
    },
    async fetchData() {
      this.$progress.start();

      let localTicketInfo = null;

      try {
        localTicketInfo = await userService.getTicketInfo(this.id, {
          expandDropdowns: true,
          withDocuments: true,
        });
      } catch (error) {
        if (error.response.status !== 403) {
          throw error;
        }

        localTicketInfo = null;
      }

      if (!localTicketInfo) {
        this.$progress.stop();
        this.$notify({
          message:
            "Chamado não encontrado, ou você não tem permissão para acessá-lo",
          timeout: 3000,
          type: "danger",
          onClose: () => this.$router.replace({ name: ROUTES.ACTIVITIES }),
        });
        return;
      }

      this.currentStep = this.getCurrentStep(
        localTicketInfo.status,
        localTicketInfo.approvalPercent,
      );

      const requests = [
        userService.getObjectItem("Ticket", this.id, "ITILSolution"),
        userService.getObjectItem("Ticket", this.id, "TicketTask"),
        userService.getObjectItem(
          "Ticket",
          this.id,
          "ITILFollowup",
          "?range=0-39999&sort=date_creation&order=DESC",
        ),
      ];

      if (this.user.approvalPresentation !== "hidden") {
        requests.push(
          userService.getObjectItem(
            "Ticket",
            this.id,
            "TicketValidation",
            "?sort=submission_date&order=ASC",
          ),
        );
      }

      // TODO handle request errors (promise status rejected or fulfilled)
      const [
        { value: ticketSolutions },
        { value: ticketTasks },
        { value: ticketFollowups },
        ticketApprovals,
      ] = await Promise.allSettled(requests);

      this.ticketInfo = {
        ...localTicketInfo,
        title: unescapeHtml(localTicketInfo.title || ""),
        category: unescapeHtml(localTicketInfo.category || ""),
        solution:
          ticketSolutions[0] &&
          ticketSolutions[0].content &&
          strip(ticketSolutions[0].content).replace(/(<([^>]+)>)/gi, ""),
      };

      this.tasks = ticketTasks.map(this.parseTask);
      this.latestTechLocationId = ticketFollowups.reduce(
        this.parseFollowup,
        null,
      );
      this.checkTicketEvaluation(ticketFollowups);

      if (
        ticketApprovals &&
        ticketApprovals.value &&
        ticketApprovals.value.length
      ) {
        this.oldestApprovalRequest = ticketApprovals.value.find(
          ({ status }) => status === NOT_ANSWERED_APPROVAL_STATUS,
        );
      }

      this.$progress.stop();
    },
    checkTicketEvaluation(ticketFollowups) {
      const solutionFollowup = ticketFollowups.find(({ content }) =>
        content.includes("Solucao"),
      );

      if (solutionFollowup) {
        const [_, techId, techName] =
          solutionFollowup.content.match(
            /Tecnico Atribuido: \(([0-9]+)\) ([A-zÀ-ú\s]+) \|/,
          ) || [];

        if (techName) {
          this.technician = {
            id: Number(techId),
            name: techName,
          };
        }
      }

      const evalutationFollowup = ticketFollowups.find(({ content }) =>
        content.includes("Nota"),
      );
      if (evalutationFollowup && this.isClosedTicket) {
        const [_, rating] = evalutationFollowup.content.match(/Nota:([0-9]+)%/);
        this.rating = Number(rating) / 20;
      }
    },
    async denyRequest() {
      if (!this.oldestApprovalRequest.comment_validation) {
        return;
      }

      this.$progress.start();

      await userService.updateObject(this.user.token, "TicketValidation", {
        input: {
          id: this.oldestApprovalRequest.id,
          status: DENIED_REQUEST_STATUS,
          comment_validation: this.oldestApprovalRequest.comment_validation,
        },
      });

      this.$progress.stop();
      this.$router.push("/activities");
    },
    async approveRequest() {
      if (
        !this.oldestApprovalRequest.comment_validation ||
        !this.ticketInfo.due_date
      ) {
        return;
      }

      this.$progress.start();

      const extensionTime = DateTime.fromFormat(
        this.ticketInfo.due_date,
        DateTimeFormats.HTML_INPUT_DATETIME,
      )
        .plus({ days: 30 })
        .toFormat(DateTimeFormats.GLPI_DATETIME);

      await userService.updateObject(this.user.token, "TicketValidation", {
        input: {
          id: this.oldestApprovalRequest.id,
          status: ACCEPTED_REQUEST_STATUS,
          comment_validation: this.oldestApprovalRequest.comment_validation,
        },
      });

      await userService.updateObject(this.user.token, "Ticket", {
        input: {
          id: this.id,
          time_to_resolve: extensionTime,
          status: 4,
          itemtype: "ticket",
        },
      });

      this.$progress.stop();
      this.$router.push("/activities");
    },
    async approveRating(rating, evaluationComment) {
      this.$progress.start();

      if (!evaluationComment) {
        evaluationComment = "Aprovado!";
      }

      evaluationComment = evaluationComment.replace(/\n/g, "<br />");
      this.ticketInfo.approvalPercent = rating * 20;

      const evaluationContent = [];
      if (this.technician) {
        evaluationContent.push(`Profissional avaliado:${this.technician.name}`);
      }
      evaluationContent.push(
        `Nota:${this.ticketInfo.approvalPercent}%`,
        `Comentário:${evaluationComment}`,
      );

      const followupContent = evaluationContent.join(" | ");

      await userService.AddTicketFollowUp(
        this.user.token,
        this.id,
        followupContent,
      );
      await userService.approveTicket(
        this.user.token,
        this.id,
        this.ticketInfo.approvalPercent,
      );

      this.$progress.stop();

      this.$router.push({ name: ROUTES.ACTIVITIES });
    },
    async denyRating(rating, evaluationComment) {
      if (!evaluationComment) {
        return;
      }

      this.$progress.start();

      evaluationComment = evaluationComment.replace(/\n/g, "<br />");
      this.ticketInfo.approvalPercent = rating * 20;

      const evaluationContent = [];
      if (this.technician) {
        evaluationContent.push(`Profissional avaliado:${this.technician.name}`);
      }
      evaluationContent.push(
        `Nota:${this.ticketInfo.approvalPercent}%`,
        `Comentário:${evaluationComment}`,
      );
      const followupContent = evaluationContent.join(" | ");

      await userService.AddTicketFollowUp(
        this.user.token,
        this.id,
        followupContent,
      );
      await userService.denyTicket(this.user.token, this.id, 0);

      this.$progress.stop();
      this.$router.push({ name: ROUTES.ACTIVITIES });
    },
    async getTechLatestLocation() {
      this.latestTechLocationId = (
        await userService.getObjectItem("Ticket", this.id, "ITILFollowup")
      ).reduce(this.parseFollowup, null);
    },
  },
};
</script>

<style>
@import url(https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css);

.step-progress {
  z-index: 1;
}
</style>
